import { FC, useEffect } from 'react'
import { WidgetProps } from '@rjsf/utils'
import styled from 'styled-components'
import Dropzone from 'react-dropzone'
import { animated, easings } from 'react-spring'
import { useIsFileDraggingAnywhereInViewport } from '../../../../../Dock/Media/DropZone/useIsFileDraggingAnywhereInViewport'
import { useAnimationStageTimelines } from '../../../../../../lib/useAnimationStageTimelines'
import { useMutationUploadMediaFile } from '../../../../../../features/media/hooks/apiControl'

import { Loader } from '../../../../../../Loader'
import { dropzoneAnimationStages } from '../../../../../Dock/Media/DropZone/animationStages'
import { Overlay } from '../../../../../Dock/Media/DropZone/Overlay/Overlay'
import { useReadyToUploadFiles } from './FileUploadWidgetService'

export const FileUploadWidget: FC<WidgetProps> = ({ onChange }) => {
  const { readyFiles, handleDrop, clearReadyFiles } = useReadyToUploadFiles()
  const isDragging = useIsFileDraggingAnywhereInViewport()
  const timelines = useAnimationStageTimelines(isDragging, dropzoneAnimationStages)
  const [, { isLoading: isAlreadyUploading }] = useMutationUploadMediaFile()

  useEffect(() => {
    onChange(
      readyFiles.map(({ sanitizedFilename, sanitizedRelativePath, dataUrl }) => ({
        filename: sanitizedFilename,
        relativePath: sanitizedRelativePath,
        data: dataUrl
      }))
    )
  }, [readyFiles, onChange])

  return (
    <>
      <StyledDropzone
        onDrop={handleDrop}
        disabled={isAlreadyUploading}
        style={{
          marginBottom: timelines.bottomZoneSlideDown
            .to(easings.easeOutCubic)
            .toValueRange([`0px`, `-${HEIGHT}`])
        }}
      >
        {({ isDragAccept, getRootProps, getInputProps }) => {
          return (
            <>
              <div {...getRootProps()}>
                <Overlay timelines={timelines} canDrop={isDragAccept} />
                <DropzoneContent
                  $canDrop={isDragAccept}
                  style={{ opacity: timelines.overlayFadeIn.toValueRange([1, 0]) }}
                >
                  {isAlreadyUploading && <Loader size="medium" />}
                  {!isAlreadyUploading && (
                    <>
                      <Text>Drag & Drop file here to upload or</Text>
                      <Button>Browse files</Button>
                      <input {...getInputProps()} />
                    </>
                  )}
                </DropzoneContent>
              </div>
            </>
          )
        }}
      </StyledDropzone>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>Uploaded files:</Text>
        <Button onClick={clearReadyFiles}>Clear all ❌</Button>
      </div>
      <FileList>
        {readyFiles.map(({ sanitizedRelativePath }) => (
          <FileListItem
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '5px',
              gap: '10px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            key={sanitizedRelativePath}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {sanitizedRelativePath}
            </span>
          </FileListItem>
        ))}
      </FileList>
    </>
  )
}

const HEIGHT = '100px'
const StyledDropzone = styled(animated(Dropzone))`
  width: 100%;
  height: ${HEIGHT};

  position: static !important; // overwrite react-dropzone lib value

  cursor: pointer;
  cursor: ${props => props.disabled && 'wait'};
`

const DropzoneContent = styled(animated.div)<{ $canDrop: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  font-weight: bold;

  background: ${({ theme }) => theme.colors.silverLight};
`

const Text = styled.div`
  color: ${({ theme }) => theme.colors.greyDark};
  font-size: 14px;
`

const Button = styled.div`
  cursor: pointer;
  border-color: ${({ theme }) => theme.colors.primary};
  border-width: 1px;
  border-style: solid;
  color: ${({ theme }) => theme.colors.primary};
  transition: 0.2s;

  padding: 11px;
  margin: 8px;
  border-radius: 5px;

  ${DropzoneContent}:hover & {
    background: ${({ theme }) => theme.colors.white};
    text-decoration: none;
  }
`
const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-family: Arial, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FileListItem = styled.li`
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  color: #333;

  &::before {
    content: '📄';
    margin-right: 8px;
  }
`
